/**
 * ESP pump housing combination optimizer.
 * Finds a mix of housing types that:
 * - Meets or slightly exceeds required stages (within tolerance)
 * - Minimizes number of housings (physical joints)
 * - Minimizes number of distinct housing types (simpler stack)
 * - Optionally obeys max total length
 */

export type HousingInput = {
  id: string;
  stage_count: number | null;
  length_inches?: number | null;
  weight_pounds?: number | null;
};

export type HousingChoice<T extends HousingInput = HousingInput> = {
  housing: T;
  count: number;
};

export type OptimizerOptions = {
  /** Fixed-frequency designs prioritize stage fit over minimizing joints. */
  prioritizeStageFit?: boolean;
  /** Bound the installed assembly size when searching stage-fit alternatives. */
  maxHousings?: number;
  /** Allow total stages up to requiredStages + tolerance (still minimize overshoot). */
  toleranceStages?: number;
  /** Reject combinations whose total length (inches) would exceed this. */
  maxLengthInches?: number;
};

/**
 * Find a combination of housings that meets requiredStages while minimizing
 * (1) number of housings, (2) number of housing types, (3) overshoot.
 * Optionally enforces maxLengthInches.
 */
export function findBestHousingCombination<T extends HousingInput>(
  availableHousings: T[],
  requiredStages: number,
  options: OptimizerOptions = {}
): HousingChoice<T>[] {
  const { toleranceStages = 0, maxLengthInches } = options;
  const targetMin = requiredStages;
  const maxOvershoot =
    toleranceStages > 0 ? toleranceStages : Number.POSITIVE_INFINITY;

  if (availableHousings.length === 0) return [];
  const housings = availableHousings.map(h => ({
    h,
    sc: Math.max(1, h.stage_count ?? 1),
    len: Number(h.length_inches) || 0
  }));

  type Candidate = {
    totalHousings: number;
    numTypes: number;
    overshoot: number;
    totalStages: number;
    totalLengthInches: number;
    counts: Map<T, number>;
  };

  function lengthOk(c: Candidate): boolean {
    if (options.maxHousings != null && c.totalHousings > options.maxHousings) return false;
    if (maxLengthInches == null || maxLengthInches <= 0) return true;
    return c.totalLengthInches <= maxLengthInches;
  }

  function overshootOk(c: Candidate): boolean {
    return c.overshoot <= maxOvershoot;
  }

  function score(c: Candidate): [number, number, number] {
    if (options.prioritizeStageFit) return [c.overshoot, c.totalHousings, c.numTypes];
    return [c.totalHousings, c.numTypes, c.overshoot];
  }

  function isBetter(a: Candidate, b: Candidate): boolean {
    if (!lengthOk(a)) return false;
    if (!lengthOk(b)) return true;
    if (!overshootOk(a)) return false;
    if (!overshootOk(b)) return true;
    const sa = score(a);
    const sb = score(b);
    if (sa[0] !== sb[0]) return sa[0] < sb[0];
    if (sa[1] !== sb[1]) return sa[1] < sb[1];
    return sa[2] < sb[2];
  }

  let best: Candidate | null = null;

  // Single type
  for (const { h, sc, len } of housings) {
    const n = Math.ceil(targetMin / sc);
    const totalStages = n * sc;
    const totalLengthInches = n * len;
    const c: Candidate = {
      totalHousings: n,
      numTypes: 1,
      overshoot: Math.max(0, totalStages - targetMin),
      totalStages,
      totalLengthInches,
      counts: new Map([[h, n]])
    };
    if (
      totalStages >= targetMin &&
      lengthOk(c) &&
      overshootOk(c) &&
      (best === null || isBetter(c, best))
    )
      best = c;
  }

  // Two types
  for (let i = 0; i < housings.length; i++) {
    for (let j = i + 1; j < housings.length; j++) {
      const hi = housings[i];
      const hj = housings[j];
      const maxNi = Math.min(Math.ceil(targetMin / hi.sc), options.maxHousings ?? Infinity);
      for (let ni = 0; ni <= maxNi; ni++) {
        const remain = targetMin - ni * hi.sc;
        const nj = remain <= 0 ? 0 : Math.ceil(remain / hj.sc);
        const totalStages = ni * hi.sc + nj * hj.sc;
        if (totalStages < targetMin) continue;
        const totalHousings = ni + nj;
        const totalLengthInches = ni * hi.len + nj * hj.len;
        const counts = new Map<T, number>();
        if (ni > 0) counts.set(hi.h, ni);
        if (nj > 0) counts.set(hj.h, nj);
        const c: Candidate = {
          totalHousings,
          numTypes: counts.size,
          overshoot: totalStages - targetMin,
          totalStages,
          totalLengthInches,
          counts
        };
        if (
          lengthOk(c) &&
          overshootOk(c) &&
          (best === null || isBetter(c, best))
        )
          best = c;
      }
    }
  }

  // Three types (bounded to avoid explosion)
  const maxPerType = (x: (typeof housings)[0]) => Math.ceil(targetMin / x.sc);
  for (let i = 0; i < housings.length; i++) {
    for (let j = i + 1; j < housings.length; j++) {
      for (let k = j + 1; k < housings.length; k++) {
        const hi = housings[i];
        const hj = housings[j];
        const hk = housings[k];
        const capI = Math.min(maxPerType(hi), 50, options.maxHousings ?? Infinity);
        const capJ = Math.min(maxPerType(hj), 50, options.maxHousings ?? Infinity);
        for (let ni = 0; ni <= capI; ni++) {
          for (let nj = 0; nj <= capJ; nj++) {
            const remain = targetMin - ni * hi.sc - nj * hj.sc;
            const nk = remain <= 0 ? 0 : Math.ceil(remain / hk.sc);
            const totalStages = ni * hi.sc + nj * hj.sc + nk * hk.sc;
            if (totalStages < targetMin) continue;
            const totalHousings = ni + nj + nk;
            const totalLengthInches =
              ni * hi.len + nj * hj.len + nk * hk.len;
            const counts = new Map<T, number>();
            if (ni > 0) counts.set(hi.h, ni);
            if (nj > 0) counts.set(hj.h, nj);
            if (nk > 0) counts.set(hk.h, nk);
            const c: Candidate = {
              totalHousings,
              numTypes: counts.size,
              overshoot: totalStages - targetMin,
              totalStages,
              totalLengthInches,
              counts
            };
            if (
              lengthOk(c) &&
              overshootOk(c) &&
              (best === null || isBetter(c, best))
            )
              best = c;
          }
        }
      }
    }
  }

  if (best === null) return [];

  const result: HousingChoice<T>[] = [];
  for (const [housing, count] of best.counts) {
    if (count > 0) result.push({ housing, count });
  }
  return result;
}
